import { useQuery } from "@tanstack/react-query";

import {
  IAiPromptTemplate,
  IAiPromptTemplateListItem,
} from "../../../@types/admin/AdminAiPromptTemplate";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { aiPromptTemplatesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const AI_PROMPT_TEMPLATES_QUERY_KEY = "admin-ai-prompt-templates";

export function useAiPromptTemplates() {
  const { data, isLoading, isError } = useQuery<
    IPaginatedResponse<IAiPromptTemplateListItem[]>
  >({
    queryKey: [AI_PROMPT_TEMPLATES_QUERY_KEY],
    queryFn: async () =>
      ensureOk(await aiPromptTemplatesService.getAiPromptTemplates()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { aiPromptTemplates: data?.result ?? [], isLoading, isError };
}

/**
 * One template with its body. Idle until an id is given — the list rows carry
 * everything except `template`, so this only runs when the editor opens.
 */
export function useAiPromptTemplate(id: string | undefined) {
  const { data, isLoading, isError } = useQuery<IAiPromptTemplate>({
    queryKey: ["admin-ai-prompt-template", id],
    queryFn: async () =>
      ensureOk(await aiPromptTemplatesService.getAiPromptTemplateById(id!)),
    enabled: Boolean(id),
    refetchOnMount: "always",
  });

  return { aiPromptTemplate: data, isLoading, isError };
}
